import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, defaultApiParamStr, queryClient } from '@/_shared';

import { productAndServicesQueryEnhancer } from './converters';
import type {
  ProductAndServicesAllQuerySchema,
  ProductAndServicesCreateSchema,
  ProductAndServicesMutationSchema,
  ProductAndServicesQuerySchema,
} from './schemas/products-and-services';
import { productAndServicesQuerySchema } from './schemas/products-and-services';

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, ProductAndServicesCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/products/',
        payload,
      }),
  });

export function getProductsAndServicesQuery(apiParamStr = defaultApiParamStr) {
  return {
    queryKey: ['PRODUCT_SERVICES', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<ProductAndServicesAllQuerySchema>({
        endpoint: '/api/v1/products',
        params: `${apiParamStr}&include=category,price_frequency`,
      });

      return res;
    },
  };
}

export function useGetProductServices(apiParamStr: string) {
  return useSuspenseQuery(getProductsAndServicesQuery(apiParamStr));
}

export const useGetProductServicesMutation: UseControlledMutation<ProductAndServicesQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['PRODUCT_SERVICES_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<ProductAndServicesAllQuerySchema>({
            endpoint: '/api/v1/products',
            params: { search, per_page, include: 'category,price_frequency,unit_sell_by' },
          });

          return orderBy(res?.data.map((item) => productAndServicesQueryEnhancer(item)) || [], ['name']);
        },
      });
    },
  });

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['PRODUCT_SERVICES'] });
};

export function useGetProductService(id: string) {
  return useSuspenseQuery(getProductServiceQuery(id));
}

export function getProductServiceQuery(id: string) {
  return {
    queryKey: ['PRODUCT_SERVICE', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: ProductAndServicesQuerySchema }>({
        // manufacturer
        endpoint: `/api/v1/products/${id}?include=category,price_frequency,unit_sell_by,manufacturer`,
        responseSchema: productAndServicesQuerySchema,
        preventAuditLog: true,
      });

      return res;
    },
  };
}

export const invalidateProductServicesQuery = (id: string) => {
  queryClient.invalidateQueries({ queryKey: ['PRODUCT_SERVICES', id] });
};

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: ProductAndServicesQuerySchema }, ApiError, ProductAndServicesMutationSchema>({
    mutationFn: ({ id: bId, ...payload }) =>
      apiClient.request({
        endpoint: `/api/v1/products/${id || bId}`,
        method: 'PUT',
        payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['AUDIT_REVISION_HISTORY', 'products', id] });
    },
  });

export const usePatchMutation = () =>
  useMutation<{ data: ProductAndServicesQuerySchema }, ApiError, Partial<ProductAndServicesMutationSchema>>({
    mutationFn: ({ id, ...payload }) =>
      apiClient.request({
        endpoint: `/api/v1/products/${id}`,
        method: 'PATCH',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['PRODUCT_SERVICE_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/products/${id}`,
        method: 'DELETE',
      }),
  });
